/**
=========================================================
* Material Dashboard 3 PRO React - v2.3.0
=========================================================
*/

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// @mui material components
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import { keyframes } from "@mui/material/styles";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 3 PRO React examples
import PageLayout from "examples/LayoutContainers/PageLayout";

// Context
import { useBranding, FRESENIUS } from "contextBranding";

// Services
import deviceService from "services/device";

// Using theme colors from design system

// Loading animations
const pulseBorder = keyframes`
  0%, 100% {
    border-color: rgba(77, 182, 172, 0.3);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(77, 182, 172, 0.1);
  }
  50% {
    border-color: rgba(77, 182, 172, 0.8);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(77, 182, 172, 0.6);
  }
`;

const glowIcon = keyframes`
  0%, 100% {
    box-shadow: 0 4px 15px rgba(38, 166, 154, 0.4);
  }
  50% {
    box-shadow: 0 4px 25px rgba(38, 166, 154, 0.7), 0 0 20px rgba(77, 182, 172, 0.5);
  }
`;

const pulseOpacity = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
`;

function DeviceSelection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isFreseniusBranding, appendBrandingSearch } = useBranding();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectingDeviceId, setConnectingDeviceId] = useState(null);
  const [connectionPhase, setConnectionPhase] = useState(null); // 'disconnecting', 'waiting', 'connecting'
  const [connectionError, setConnectionError] = useState(null);

  // On page load: Fetch available devices by calling the Bun server endpoint
  useEffect(() => {
    const fetchDevices = async () => {
      try {
        setLoading(true);
        console.log("[DeviceSelection] Fetching devices... Loading state:", true);
        // Call the device service which calls the Bun server /devices/scan endpoint
        const availableDevices = await deviceService.getAvailableDevices();
        console.log("[DeviceSelection] Received devices:", availableDevices);
        setDevices(availableDevices);
      } catch (error) {
        console.error("[DeviceSelection] Error fetching devices:", error);
        // On error, still show empty list or handle error state
        setDevices([]);
      } finally {
        // Add a minimum delay to ensure loading animation is visible
        await new Promise((resolve) => setTimeout(resolve, 500));
        setLoading(false);
        console.log("[DeviceSelection] Loading complete. Loading state:", false);
      }
    };

    // Execute on component mount (page load)
    fetchDevices();
  }, []);

  const handleDeviceClick = async (device) => {
    // Only allow connection for reachable devices
    if (!device.reachable || connectingDeviceId) {
      return;
    }

    try {
      setConnectingDeviceId(device.id);
      setConnectionError(null);
      setConnectionPhase("disconnecting");
      
      console.log(`[DeviceSelection] Disconnecting from any existing device...`);
      
      // Step 1: Disconnect from any current device
      try {
        await deviceService.disconnectDevice();
        console.log(`[DeviceSelection] Successfully disconnected`);
      } catch (disconnectError) {
        // If disconnect fails, it might mean no device was connected - that's okay
        console.log(`[DeviceSelection] Disconnect result (may be expected):`, disconnectError.message);
      }
      
      // Step 2: Wait 1-2 seconds before connecting
      setConnectionPhase("waiting");
      await new Promise((resolve) => setTimeout(resolve, 1500)); // Wait 1.5 seconds
      
      // Step 3: Connect to the new device
      setConnectionPhase("connecting");
      console.log(`[DeviceSelection] Connecting to device: ${device.name} (${device.host}:${device.port})`);
      
      const result = await deviceService.connectDevice(device.host, device.port);
      
      console.log(`[DeviceSelection] Successfully connected to device:`, result);
      
      // Navigate to connect page after successful connection
      navigate(appendBrandingSearch("/connect"));
    } catch (error) {
      console.error(`[DeviceSelection] Error connecting to device:`, error);
      setConnectionError({
        deviceId: device.id,
        message: error.message || t("device.failedToConnect"),
      });
      
      // Clear error after 5 seconds
      setTimeout(() => {
        setConnectionError(null);
      }, 5000);
    } finally {
      setConnectingDeviceId(null);
      setConnectionPhase(null);
    }
  };

  // Debug: Log loading state on render
  console.log("[DeviceSelection] Render - loading:", loading, "devices:", devices.length);

  return (
    <PageLayout background="default">
      <MDBox
        position="absolute"
        width="100%"
        minHeight="100vh"
        sx={(theme) => ({
          backgroundColor: isFreseniusBranding
            ? FRESENIUS.blueDark
            : theme.palette.backgroundSbi.secondary,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        })}
      />
      
      <MDBox
        px={1}
        width="100%"
        height="100vh"
        mx="auto"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        position="relative"
        zIndex={1}
      >
        <MDBox width="100%" maxWidth="900px" px={3}>
          {/* Header */}
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={5}
          >
            <MDBox>
              <MDTypography 
                variant="h3" 
                fontWeight="bold" 
                color="white"
                mb={0.5}
                sx={(theme) => ({
                  background: isFreseniusBranding
                    ? `linear-gradient(135deg, ${FRESENIUS.white} 0%, ${FRESENIUS.blueLight} 100%)`
                    : `linear-gradient(135deg, ${theme.palette.accent.tealLight} 0%, ${theme.palette.accent.cyan} 100%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                })}
              >
                {t("device.selectDevice")}
              </MDTypography>
              <MDTypography variant="body2" color="white" opacity={0.7}>
                {t("device.chooseDevice")}
              </MDTypography>
            </MDBox>
            <MDButton
              variant="outlined"
              color="light"
              onClick={() => navigate("/landing")}
              sx={(theme) => ({
                borderColor: "rgba(255, 255, 255, 0.3)",
                color: "white",
                "&:hover": {
                  borderColor: isFreseniusBranding ? FRESENIUS.borderStrong : theme.palette.accent.tealLight,
                  backgroundColor: isFreseniusBranding
                    ? `${FRESENIUS.blueLight}26`
                    : "rgba(77, 182, 172, 0.1)",
                },
              })}
            >
              <Icon sx={{ mr: 1 }}>arrow_back</Icon>
              {t("device.back")}
            </MDButton>
          </MDBox>

          {/* Device List */}
          {loading ? (
            <MDBox display="flex" flexDirection="column" gap={3}>
              {/* Loading Device Card */}
              <MDBox
                sx={(theme) => ({
                  borderRadius: "20px",
                  padding: "2rem",
                  background: isFreseniusBranding
                    ? `linear-gradient(135deg, ${FRESENIUS.blueDark}99 0%, ${FRESENIUS.blue}66 100%)`
                    : `linear-gradient(135deg, ${theme.palette.backgroundSbi.default}99 0%, ${theme.palette.backgroundSbi.default}66 100%)`,
                  border: isFreseniusBranding
                    ? `2px solid ${FRESENIUS.border}`
                    : `2px solid ${theme.palette.accent.tealLight}4D`,
                  boxShadow: isFreseniusBranding
                    ? "0 4px 20px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)"
                    : `0 4px 20px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(77, 182, 172, 0.1)`,
                  animation: `${pulseBorder} 2s ease-in-out infinite, ${pulseOpacity} 2s ease-in-out infinite`,
                })}
              >
                <MDBox display="flex" alignItems="flex-start" gap={3}>
                  {/* Device Icon with Loading Animation */}
                  <MDBox
                    sx={(theme) => ({
                      width: "72px",
                      height: "72px",
                      borderRadius: "16px",
                      background: isFreseniusBranding
                        ? `linear-gradient(135deg, ${FRESENIUS.blue} 0%, ${FRESENIUS.blueLight} 100%)`
                        : `linear-gradient(135deg, ${theme.palette.accent.tealAccent} 0%, ${theme.palette.accent.cyan} 100%)`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      boxShadow: isFreseniusBranding
                        ? "0 4px 15px rgba(0, 84, 159, 0.4)"
                        : `0 4px 15px rgba(38, 166, 154, 0.4)`,
                      animation: `${glowIcon} 2s ease-in-out infinite`,
                      position: "relative",
                    })}
                  >
                    <CircularProgress
                      size={40}
                      sx={{
                        position: "absolute",
                        color: "rgba(255, 255, 255, 0.3)",
                        "& .MuiCircularProgress-circle": {
                          strokeLinecap: "round",
                        },
                      }}
                    />
                    <Icon sx={{ color: "white", fontSize: "2.5rem", position: "relative", zIndex: 1 }}>
                      devices_other
                    </Icon>
                  </MDBox>

                  {/* Device Info */}
                  <MDBox flex={1} minWidth={0}>
                    {/* Device Name and Status Row */}
                    <MDBox display="flex" alignItems="center" gap={1.5} mb={1.5} flexWrap="wrap">
                      <MDTypography
                        variant="h5"
                        fontWeight="bold"
                        color="white"
                        sx={{
                          fontSize: "1.5rem",
                        }}
                      >
                        {t("device.scanningForDevices")}
                      </MDTypography>
                      
                      {/* Loading Badge */}
                      <MDBox
                        sx={(theme) => ({
                          padding: "0.35rem 0.75rem",
                          borderRadius: "8px",
                          background: isFreseniusBranding
                            ? `linear-gradient(135deg, ${FRESENIUS.blueLight}40 0%, ${FRESENIUS.blue}40 100%)`
                            : `linear-gradient(135deg, ${theme.palette.accent.tealLight}40 0%, ${theme.palette.accent.cyan}40 100%)`,
                          border: isFreseniusBranding
                            ? `1px solid ${FRESENIUS.border}`
                            : `1px solid ${theme.palette.accent.tealLight}99`,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        })}
                      >
                        <CircularProgress
                          size={12}
                          sx={(theme) => ({
                            color: isFreseniusBranding ? FRESENIUS.white : theme.palette.accent.tealLight,
                            "& .MuiCircularProgress-circle": {
                              strokeLinecap: "round",
                            },
                          })}
                        />
                        <MDTypography
                          variant="caption"
                          sx={(theme) => ({
                            color: isFreseniusBranding ? FRESENIUS.white : theme.palette.accent.tealLight,
                            fontSize: "0.75rem",
                            fontWeight: 600,
                          })}
                        >
                          Connecting
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  </MDBox>

                  {/* Action Icon with Loading Animation */}
                  <MDBox
                    sx={(theme) => ({
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "48px",
                      height: "48px",
                      borderRadius: "12px",
                      background: isFreseniusBranding
                        ? `linear-gradient(135deg, ${FRESENIUS.blue}33 0%, ${FRESENIUS.blueLight}33 100%)`
                        : `linear-gradient(135deg, ${theme.palette.accent.tealAccent}33 0%, ${theme.palette.accent.cyan}33 100%)`,
                      transition: "all 0.3s ease",
                    })}
                  >
                    <CircularProgress
                      size={24}
                      sx={(theme) => ({
                        color: isFreseniusBranding ? FRESENIUS.white : theme.palette.accent.tealLight,
                        "& .MuiCircularProgress-circle": {
                          strokeLinecap: "round",
                        },
                      })}
                    />
                  </MDBox>
                </MDBox>
              </MDBox>
            </MDBox>
          ) : devices.length === 0 ? (
            <MDBox
              sx={(theme) => ({
                borderRadius: "20px",
                padding: "4rem 2rem",
                background: isFreseniusBranding
                  ? `linear-gradient(135deg, ${FRESENIUS.blueDark}99 0%, ${FRESENIUS.blue}66 100%)`
                  : `linear-gradient(135deg, ${theme.palette.backgroundSbi.default}99 0%, ${theme.palette.backgroundSbi.default}66 100%)`,
                border: isFreseniusBranding
                  ? `2px solid ${FRESENIUS.border}`
                  : `2px solid ${theme.palette.accent.tealLight}4D`,
                textAlign: "center",
              })}
            >
              <Icon
                sx={(theme) => ({
                  color: isFreseniusBranding ? FRESENIUS.white : theme.palette.accent.tealLight,
                  fontSize: "4rem",
                  mb: 2,
                  opacity: 0.7,
                })}
              >
                devices_other
              </Icon>
              <MDTypography 
                variant="h5" 
                color="white" 
                mb={1}
                fontWeight="bold"
              >
                No devices available
              </MDTypography>
              <MDTypography variant="body2" color="white" opacity={0.7}>
                Please check your connection or try again later.
              </MDTypography>
            </MDBox>
          ) : (
            <MDBox display="flex" flexDirection="column" gap={3}>
              {devices.map((device) => {
                const isConnecting = connectingDeviceId === device.id;
                const hasError = connectionError?.deviceId === device.id;
                const isProcessing = device.state_code === 0;
                
                return (
                  <MDBox
                    key={device.id}
                    onClick={() => handleDeviceClick(device)}
                    sx={(theme) => ({
                      cursor: device.reachable && !isConnecting ? "pointer" : "not-allowed",
                      borderRadius: "20px",
                      padding: "2rem",
                      background: device.reachable
                        ? isFreseniusBranding
                          ? `linear-gradient(135deg, ${FRESENIUS.blueDark}99 0%, ${FRESENIUS.blue}66 100%)`
                          : `linear-gradient(135deg, ${theme.palette.backgroundSbi.default}99 0%, ${theme.palette.backgroundSbi.default}66 100%)`
                        : isFreseniusBranding
                        ? `linear-gradient(135deg, ${FRESENIUS.blue}4D 0%, ${FRESENIUS.blue}33 100%)`
                        : `linear-gradient(135deg, ${theme.palette.backgroundSbi.default}4D 0%, ${theme.palette.backgroundSbi.default}33 100%)`,
                      border: hasError
                        ? `2px solid rgba(244, 67, 54, 0.6)`
                        : device.reachable
                        ? isFreseniusBranding
                          ? `2px solid ${FRESENIUS.border}`
                          : `2px solid ${theme.palette.accent.tealLight}4D`
                        : isFreseniusBranding
                        ? `2px dashed ${FRESENIUS.border}`
                        : `2px dashed ${theme.palette.accent.tealLight}33`,
                      opacity: device.reachable && !isConnecting ? 1 : 0.5,
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      boxShadow: device.reachable
                        ? isFreseniusBranding
                          ? "0 4px 20px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)"
                          : `0 4px 20px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(77, 182, 172, 0.1)`
                        : "none",
                      "&:hover": device.reachable && !isConnecting
                        ? isFreseniusBranding
                          ? {
                              transform: "translateY(-4px) scale(1.01)",
                              borderColor: FRESENIUS.borderStrong,
                              backgroundColor: `${FRESENIUS.blueLight}26`,
                              boxShadow: `0 8px 30px rgba(0, 0, 0, 0.4), 0 0 0 1px ${FRESENIUS.border}66`,
                            }
                          : {
                              transform: "translateY(-4px) scale(1.01)",
                              borderColor: theme.palette.accent.tealLight,
                              backgroundColor: `${theme.palette.accent.tealLight}26`,
                              boxShadow: `0 8px 30px rgba(0, 0, 0, 0.4), 0 0 0 1px ${theme.palette.accent.tealLight}66`,
                            }
                        : {},
                    })}
                  >
                    <MDBox display="flex" alignItems="flex-start" gap={3}>
                      {/* Device Icon */}
                      <MDBox
                        sx={(theme) => ({
                          width: "72px",
                          height: "72px",
                          borderRadius: "16px",
                          background: device.reachable
                            ? isFreseniusBranding
                              ? `linear-gradient(135deg, ${FRESENIUS.blue} 0%, ${FRESENIUS.blueLight} 100%)`
                              : `linear-gradient(135deg, ${theme.palette.accent.tealAccent} 0%, ${theme.palette.accent.cyan} 100%)`
                            : isFreseniusBranding
                            ? `linear-gradient(135deg, ${FRESENIUS.blueLight}4D 0%, ${FRESENIUS.blue}33 100%)`
                            : `linear-gradient(135deg, ${theme.palette.accent.tealLight}4D 0%, ${theme.palette.accent.tealLight}33 100%)`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          boxShadow: device.reachable
                            ? isFreseniusBranding
                              ? "0 4px 15px rgba(0, 84, 159, 0.4)"
                              : `0 4px 15px rgba(38, 166, 154, 0.4)`
                            : "none",
                        })}
                      >
                        <Icon sx={{ color: "white", fontSize: "2.5rem" }}>
                          {device.icon}
                        </Icon>
                      </MDBox>

                      {/* Device Info */}
                      <MDBox flex={1} minWidth={0}>
                        {/* Device Name and Status Row */}
                        <MDBox display="flex" alignItems="center" gap={1.5} mb={1.5} flexWrap="wrap">
                          <MDTypography
                            variant="h5"
                            fontWeight="bold"
                            color="white"
                            sx={{
                              fontSize: "1.5rem",
                            }}
                          >
                            {device.name}
                          </MDTypography>
                          
                          {/* Status Badges */}
                          {device.reachable && (
                            <MDBox display="flex" gap={1} flexWrap="wrap" alignItems="center">
                              {/* Latency Badge */}
                              {device.latency && (
                                <MDBox
                                  sx={(theme) => ({
                                    padding: "0.35rem 0.75rem",
                                    borderRadius: "8px",
                                    background: isFreseniusBranding
                                      ? `linear-gradient(135deg, ${FRESENIUS.blueLight}40 0%, ${FRESENIUS.blue}40 100%)`
                                      : `linear-gradient(135deg, ${theme.palette.accent.tealLight}40 0%, ${theme.palette.accent.cyan}40 100%)`,
                                    border: isFreseniusBranding
                                      ? `1px solid ${FRESENIUS.border}`
                                      : `1px solid ${theme.palette.accent.tealLight}99`,
                                  })}
                                >
                                  <MDTypography
                                    variant="caption"
                                    sx={(theme) => ({
                                      color: isFreseniusBranding ? FRESENIUS.white : theme.palette.accent.tealLight,
                                      fontSize: "0.75rem",
                                      fontWeight: 600,
                                    })}
                                  >
                                    {device.latency}ms
                                  </MDTypography>
                                </MDBox>
                              )}
                              
                              {/* State Badge */}
                              {device.state && (
                                <MDBox
                                  sx={(theme) => ({
                                    padding: "0.35rem 0.75rem",
                                    borderRadius: "8px",
                                    background: isProcessing
                                      ? isFreseniusBranding
                                        ? `linear-gradient(135deg, ${FRESENIUS.blueLight}40 0%, ${FRESENIUS.blue}40 100%)`
                                        : `linear-gradient(135deg, ${theme.palette.accent.tealLight}40 0%, ${theme.palette.accent.cyan}40 100%)`
                                      : `linear-gradient(135deg, rgba(255, 152, 0, 0.25) 0%, rgba(255, 193, 7, 0.25) 100%)`,
                                    border: isProcessing
                                      ? isFreseniusBranding
                                        ? `1px solid ${FRESENIUS.border}`
                                        : `1px solid ${theme.palette.accent.tealLight}99`
                                      : "1px solid rgba(255, 152, 0, 0.6)",
                                  })}
                                >
                                  <MDTypography
                                    variant="caption"
                                    sx={(theme) => ({
                                      color: isProcessing
                                        ? (isFreseniusBranding ? FRESENIUS.white : theme.palette.accent.tealLight)
                                        : "#ffb74d",
                                      fontSize: "0.75rem",
                                      fontWeight: 600,
                                    })}
                                  >
                                    {device.state}
                                  </MDTypography>
                                </MDBox>
                              )}
                              
                              {/* Signals Badge */}
                              {device.signals_valid !== null && (
                                <MDBox
                                  sx={(theme) => ({
                                    padding: "0.35rem 0.75rem",
                                    borderRadius: "8px",
                                    background: device.signals_valid
                                      ? isFreseniusBranding
                                        ? `linear-gradient(135deg, ${FRESENIUS.blueLight}40 0%, ${FRESENIUS.blue}40 100%)`
                                        : `linear-gradient(135deg, ${theme.palette.accent.tealLight}40 0%, ${theme.palette.accent.cyan}40 100%)`
                                      : `linear-gradient(135deg, rgba(244, 67, 54, 0.25) 0%, rgba(229, 57, 53, 0.25) 100%)`,
                                    border: device.signals_valid
                                      ? isFreseniusBranding
                                        ? `1px solid ${FRESENIUS.border}`
                                        : `1px solid ${theme.palette.accent.tealLight}99`
                                      : "1px solid rgba(244, 67, 54, 0.6)",
                                  })}
                                >
                                  <MDTypography
                                    variant="caption"
                                    sx={(theme) => ({
                                      color: device.signals_valid
                                        ? (isFreseniusBranding ? FRESENIUS.white : theme.palette.accent.tealLight)
                                        : "#ef5350",
                                      fontSize: "0.75rem",
                                      fontWeight: 600,
                                    })}
                                  >
                                    {device.signals_valid ? "✓ Signals" : "✗ Signals"}
                                  </MDTypography>
                                </MDBox>
                              )}
                              
                              {/* Data Badge */}
                              {device.has_data !== null && (
                                <MDBox
                                  sx={(theme) => ({
                                    padding: "0.35rem 0.75rem",
                                    borderRadius: "8px",
                                    background: device.has_data
                                      ? isFreseniusBranding
                                        ? `linear-gradient(135deg, ${FRESENIUS.blueLight}40 0%, ${FRESENIUS.blue}40 100%)`
                                        : `linear-gradient(135deg, ${theme.palette.accent.cyan}40 0%, ${theme.palette.accent.tealAccent}40 100%)`
                                      : `linear-gradient(135deg, rgba(158, 158, 158, 0.2) 0%, rgba(117, 117, 117, 0.2) 100%)`,
                                    border: device.has_data
                                      ? isFreseniusBranding
                                        ? `1px solid ${FRESENIUS.border}`
                                        : `1px solid ${theme.palette.accent.cyan}99`
                                      : "1px solid rgba(158, 158, 158, 0.4)",
                                  })}
                                >
                                  <MDTypography
                                    variant="caption"
                                    sx={(theme) => ({
                                      color: device.has_data
                                        ? (isFreseniusBranding ? FRESENIUS.white : theme.palette.accent.cyan)
                                        : "#9e9e9e",
                                      fontSize: "0.75rem",
                                      fontWeight: 600,
                                    })}
                                  >
                                    {device.has_data ? "📊 Data" : "No Data"}
                                  </MDTypography>
                                </MDBox>
                              )}
                            </MDBox>
                          )}
                        </MDBox>

                        {/* Connection Phase Status */}
                        {isConnecting && connectionPhase && (
                          <MDBox mt={1.5} display="flex" alignItems="center" gap={1}>
                            <CircularProgress
                              size={16}
                              sx={(theme) => ({
                                color: isFreseniusBranding ? FRESENIUS.white : theme.palette.accent.tealLight,
                              })}
                            />
                            <MDTypography
                              variant="caption"
                              sx={(theme) => ({
                                color: isFreseniusBranding ? FRESENIUS.white : theme.palette.accent.tealLight,
                                fontSize: "0.8rem",
                                fontWeight: 500,
                              })}
                            >
                              {connectionPhase === "disconnecting" && "Disconnecting from current device..."}
                              {connectionPhase === "waiting" && "Preparing connection..."}
                              {connectionPhase === "connecting" && "Connecting to device..."}
                            </MDTypography>
                          </MDBox>
                        )}

                        {/* Error Message */}
                        {hasError && connectionError && (
                          <MDBox 
                            mt={1.5}
                            sx={{
                              padding: "0.75rem 1rem",
                              borderRadius: "8px",
                              background: "rgba(244, 67, 54, 0.15)",
                              border: "1px solid rgba(244, 67, 54, 0.3)",
                            }}
                          >
                            <MDTypography
                              variant="caption"
                              color="#ef5350"
                              sx={{ fontSize: "0.8rem", fontWeight: 500 }}
                            >
                              {connectionError.message}
                            </MDTypography>
                          </MDBox>
                        )}
                      </MDBox>

                      {/* Action Icon */}
                      <MDBox
                        sx={(theme) => ({
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "48px",
                          height: "48px",
                          borderRadius: "12px",
                          background: device.reachable && !isConnecting
                            ? `linear-gradient(135deg, ${theme.palette.accent.tealAccent}33 0%, ${theme.palette.accent.cyan}33 100%)`
                            : "transparent",
                          transition: "all 0.3s ease",
                        })}
                      >
                        {isConnecting ? (
                          <CircularProgress size={24} sx={(theme) => ({ color: theme.palette.accent.tealLight })} />
                        ) : hasError ? (
                          <Icon sx={{ color: "#ef5350", fontSize: "1.75rem" }}>
                            error_outline
                          </Icon>
                        ) : device.reachable ? (
                          <Icon sx={(theme) => ({ color: theme.palette.accent.tealLight, fontSize: "1.75rem" })}>
                            arrow_forward
                          </Icon>
                        ) : (
                          <Icon sx={{ color: "rgba(255, 255, 255, 0.3)", fontSize: "1.75rem" }}>
                            block
                          </Icon>
                        )}
                      </MDBox>
                    </MDBox>
                  </MDBox>
                );
              })}
            </MDBox>
          )}
        </MDBox>
      </MDBox>
    </PageLayout>
  );
}

export default DeviceSelection;


